import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Popover,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { ReactComponent as InfoIcon } from "../../assets/icons/About.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/Download-Dark.svg";
import { ReactComponent as NewChat } from "../../assets/icons/NewChat-Dark.svg";
import { useChat } from "../../hooks/useChat";
import { useChatContext } from "../../contexts/ChatContext";
import { useSourceContext } from "../../contexts/SourcesContext";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import StarterPage from "./StarterPage";
import { panelStyles, chatPanelStyles } from "../PanelStyles";
import { FeedbackReaction, MessageRoles } from "../../types/frontend";
import { DOCUMENTATION_URL } from "../../constants";
import MessageActions from "./MessageActions";
import FeedbackPopover from "./FeedbackPopover";
import { isStarterPromptInCategory } from "../../hooks/useStarterPrompts";
import { PromptCategory } from "../../types/frontend";

/**
 * Props interface for the ChatPanel component
 * @interface ChatPanelProps
 * @property {string} input - Current chat query text
 * @property {(query: string) => void} setInput - Callback function to update the chat query
 */
export interface ChatPanelProps {
  input: string;
  setInput: (query: string) => void;
}

/**
 * ChatPanel component that handles the display and interaction of chat messages.
 * @param {ChatPanelProps} props - The properties for the ChatPanel component.
 * @returns {JSX.Element} The rendered ChatPanel component.
 */
const ChatPanel: React.FC<ChatPanelProps> = ({ input, setInput }) => {
  const theme = useTheme();

  // Context hooks to manage chat and source states
  const {
    messages: storeMessages,
    addMessage,
    clearMessages,
  } = useChatContext();
  const {
    clearSources,
    attachedSources,
    setAttachedSources,
    clearAttachedSources,
  } = useSourceContext();
  const { isLoading, error, sendMessage, abortGeneration, sendFeedback } =
    useChat();
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [isSearchOn, setIsSearchOn] = useState(true);
  const [previousSearchState, setPreviousSearchState] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedReaction, setSelectedReaction] =
    useState<FeedbackReaction | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? "feedback-popover" : undefined;

  const messageListRef = useRef<HTMLUListElement>(null);

  /**
   * Determines whether to search for sources based on the message content.
   * Currently, we control this only by:
   * - Setting search off if the message is included in the "about chatbot" category of starter prompts.
   * Otherwise, we use the search toggle setting.
   */
  const shouldSearchForSources = (messageContent: string): boolean => {
    if (
      isStarterPromptInCategory(
        messageContent.trim(),
        PromptCategory.AboutChatbot
      )
    ) {
      return false;
    }
    return isSearchOn;
  };

  /**
   * Copies the provided text to the clipboard and shows a snackbar notification.
   * @param {string} content - The text content to be copied.
   */
  const handleCopyText = (content: string) => {
    navigator.clipboard.writeText(content);
    setSnackbarOpen(true);
  };

  /**
   * Closes the snackbar notification.
   * @param {React.SyntheticEvent | Event} [event] - The event that triggered the close.
   * @param {string} [reason] - The reason for closing the snackbar.
   */
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  /**
   * Handles the submission of feedback for a chat message.
   *
   * @param traceId - The trace ID associated with the chat message.
   * @param feedback - The feedback to submit.
   */
  const handleSubmitFeedback = (
    traceId: string,
    reaction: FeedbackReaction,
    comment?: string,
    reasons?: string[]
  ) => {
    sendFeedback(traceId, {
      trace_id: traceId,
      score: reaction === FeedbackReaction.Up ? 1.0 : 0.0,
      reasons: reasons ?? [],
      comment: comment ?? "",
    });
  };

  /**
   * Handles the click event for the thumbs up/down feedback buttons on a chat message.
   *
   * If the same reaction is clicked again, it deselects the reaction and closes the feedback popover.
   * If a different reaction is clicked, it sets the selected reaction and opens the feedback popover anchored to the clicked element.
   *
   * @param event - The mouse event triggered by clicking the feedback button.
   * @param reaction - The type of reaction (FeedbackReaction.Up for thumbs up, FeedbackReaction.Down for thumbs down).
   * @param traceId - The trace ID associated with the chat message.
   */
  const handleThumbClick = (
    event: React.MouseEvent<HTMLElement>,
    reaction: FeedbackReaction,
    traceId: string
  ) => {
    // If the same reaction is clicked again,
    // deselect the reaction and close the feedback popover.
    if (selectedReaction === reaction) {
      setSelectedReaction(null);
      setAnchorEl(null);
    } else {
      // If a different reaction is clicked,
      // set the selected reaction and open the feedback
      // popover anchored to the clicked element, finally
      // submit the feedback to the backend.
      setSelectedReaction(reaction);
      setAnchorEl(event.currentTarget);
      handleSubmitFeedback(traceId, reaction, "", []);
    }
  };

  /**
   * Closes the feedback popover and resets the selected reaction.
   */
  const handleCloseFeedbackPopover = () => {
    setAnchorEl(null);
    setSelectedReaction(null);
  };

  // Scrolls to the bottom of the message list whenever a new message is added
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [storeMessages.length]);

  // Auto-toggle search based on attached sources
  useEffect(() => {
    if (attachedSources.length > 0) {
      // Store current search state before turning it off
      setIsSearchOn(false);
    } else if (attachedSources.length === 0) {
      // Restore previous search state when no sources are attached
      setIsSearchOn(previousSearchState);
    }
  }, [attachedSources, previousSearchState]);

  /**
   * Sends a message if the input is not empty and clears the input and attached sources.
   */
  const handleSendMessage = () => {
    if (input.trim()) {
      const messageWithTraceId = addMessage({
        role: MessageRoles.User,
        content: input,
        sources: attachedSources,
      });
      sendMessage(
        messageWithTraceId,
        storeMessages,
        shouldSearchForSources(input)
      );
      setInput("");
    }
  };

  /**
   * Opens the modal to start a new chat.
   */
  const handleOpenNewChatModal = () => {
    setIsNewChatModalOpen(true);
  };

  /**
   * Closes the new chat modal.
   */
  const handleCloseNewChatModal = () => {
    setIsNewChatModalOpen(false);
  };

  /**
   * Clears the current chat and sources, and closes the new chat modal.
   */
  const handleStartNewChat = () => {
    clearMessages();
    clearSources();
    clearAttachedSources();
    setIsNewChatModalOpen(false);
  };

  /**
   * Sends a starter prompt as a new message.
   * @param {string} promptText - The starter prompt text.
   */
  const handleStarterPromptSend = (promptText: string) => {
    if (promptText.trim()) {
      const messageWithTraceId = addMessage({
        role: MessageRoles.User,
        content: promptText,
      });
      sendMessage(
        messageWithTraceId,
        storeMessages,
        shouldSearchForSources(promptText)
      );
      setInput("");
    }
  };

  /**
   * Opens the documentation in a new browser tab.
   */
  const handleOpenDocumentation = () => {
    window.open(DOCUMENTATION_URL, "_blank", "noopener");
  };

  /**
   * Toggles the search state and updates the previous search state.
   */
  const handleToggleSearch = () => {
    setIsSearchOn((prev) => !prev);
    setPreviousSearchState((prev) => !prev);
  };

  /**
   * Removes a source from the attached sources list by its ID.
   * @param {string} sourceId - The ID of the source to be removed.
   */
  const handleRemoveSource = (sourceId: string) => {
    setAttachedSources((prev) =>
      prev.filter((source) => source.id !== sourceId)
    );
  };

  /**
   * Placeholder function for downloading chat functionality.
   */
  const handleDownloadChat = () => {
    // TODO: Implement chat download functionality
  };

  // Check if the chat has started by verifying if there are any messages
  const hasStartedChat = storeMessages.length > 0;

  return (
    <Paper elevation={0} sx={panelStyles.container}>
      {/* Chat content */}
      <Box sx={chatPanelStyles.contentContainer}>
        {/* Header */}
        <Box sx={panelStyles.header}>
          <Typography variant="h6" sx={panelStyles.headerTitle}>
            Chat
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="New chat" placement="top">
              <IconButton
                size="small"
                onClick={handleOpenNewChatModal}
                data-attr="new-chat-button"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  marginRight: 2,
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: "grey.200",
                  },
                }}
                disableRipple
              >
                <NewChat fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                <React.Fragment>
                  <div style={{ textAlign: "center" }}>
                    Coming soon:
                    <br />
                    Download chat
                  </div>
                </React.Fragment>
              }
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownloadChat}
                  data-attr="download-chat-button"
                  sx={{
                    color: "text.secondary",
                    bgcolor: "grey.100",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    marginRight: 2,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "grey.200",
                    },
                  }}
                  disableRipple
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Help" placement="top">
              <IconButton
                size="medium"
                onClick={handleOpenDocumentation}
                data-attr="help-button"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  marginRight: 2,
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: "grey.200",
                  },
                }}
                disableRipple
              >
                <InfoIcon style={{ color: "grey.200" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Starter page */}
        {!hasStartedChat && (
          <StarterPage onPromptSend={handleStarterPromptSend} />
        )}

        {/* Message list */}
        {hasStartedChat && (
          <List
            sx={{
              ...chatPanelStyles.messageList,
              overflowX: "hidden",
              overflowY: "visible",
            }}
            ref={messageListRef}
          >
            {storeMessages.map((chat, index) => {
              const isAssistant = chat.role === "assistant";
              const isLastAssistantMessage =
                isAssistant && index === storeMessages.length - 1;
              const isActivelyResponding = isLastAssistantMessage && isLoading;
              const hasContentStarted = chat.content && chat.content.length > 0;

              const messageBackgroundColor = isAssistant
                ? isActivelyResponding
                  ? hasContentStarted
                    ? "#eff6f4"
                    : theme.palette.common.white
                  : "#eff6f4"
                : theme.palette.materiom.seaweed;

              const messageTextColor = isAssistant
                ? theme.palette.text.primary
                : theme.palette.primary.contrastText;

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isAssistant ? "flex-start" : "flex-end",
                    mb: 1,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isAssistant ? "row" : "row-reverse",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    {/* Assistant Avatar */}
                    {isAssistant && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mr: 1,
                        }}
                      >
                        <img
                          src="/materiom_logo.png"
                          alt="Materiom Logo"
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            display: "block",
                            padding: 5,
                            border: "2px solid rgb(236, 236, 236)",
                          }}
                        />
                      </Box>
                    )}
                    {/* Message Content */}
                    <ListItem
                      sx={{
                        ...chatPanelStyles.messageItem(chat.role),
                        justifyContent: isAssistant ? "flex-start" : "flex-end",
                        maxWidth: "100%",
                        bgcolor: messageBackgroundColor,
                        color: messageTextColor,
                      }}
                    >
                      {/* Message Content */}
                      <ChatMessage
                        chat={chat}
                        styles={chatPanelStyles}
                        isLastMessage={index === storeMessages.length - 1}
                        isLoading={isLoading}
                      />
                    </ListItem>
                  </Box>
                  {/* Message Actions */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: isAssistant ? "flex-start" : "flex-end",
                      ml: isAssistant ? "56px" : 0,
                      mt: isAssistant ? "-10px" : 0,
                    }}
                  >
                    <MessageActions
                      isAssistantMessage={chat.role === MessageRoles.Assistant}
                      isLoading={isLoading}
                      isLastMessage={index === storeMessages.length - 1}
                      selectedReaction={selectedReaction}
                      onCopy={() => handleCopyText(chat.content)}
                      onThumbClick={(event, reaction) =>
                        handleThumbClick(event, reaction, chat.traceId ?? "")
                      }
                    />
                  </Box>
                  {/* Feedback Popover */}
                  {isPopoverOpen && (
                    <Popover
                      id={popoverId}
                      open={isPopoverOpen}
                      anchorEl={anchorEl}
                      onClose={handleCloseFeedbackPopover}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                      <FeedbackPopover
                        reaction={selectedReaction!}
                        score={
                          selectedReaction === FeedbackReaction.Up ? 1.0 : 0.0
                        }
                        traceId={chat.traceId ?? ""}
                        onClose={handleCloseFeedbackPopover}
                        handleSubmitFeedback={(
                          traceId,
                          reaction,
                          comment,
                          reasons
                        ) =>
                          handleSubmitFeedback(
                            traceId,
                            reaction,
                            comment,
                            reasons
                          )
                        }
                      />
                    </Popover>
                  )}
                  {/* Snackbar for copying text */}
                  <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    <Alert
                      onClose={handleSnackbarClose}
                      severity="success"
                      sx={{ width: "100%" }}
                    >
                      Response text copied to clipboard!
                    </Alert>
                  </Snackbar>
                </Box>
              );
            })}
          </List>
        )}
        {error && (
          <ListItem>
            <Alert
              severity="error"
              sx={{ width: "100%" }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => handleSendMessage()}
                  disableRipple
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </ListItem>
        )}
      </Box>
      {/* Chat Input */}
      <Box sx={chatPanelStyles.inputContainer}>
        <ChatInput
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
          handleStopGeneration={abortGeneration}
          styles={chatPanelStyles}
          isSearchOn={isSearchOn}
          onToggleSearch={handleToggleSearch}
          attachedSources={attachedSources}
          onRemoveSource={handleRemoveSource}
        />
      </Box>

      {/* New Chat Modal */}
      <Dialog
        open={isNewChatModalOpen}
        onClose={handleCloseNewChatModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="new-chat-dialog-title">Start a new chat</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            This will clear your current chat history. Are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewChatModal} disableRipple>
            Cancel
          </Button>
          <Button
            onClick={handleStartNewChat}
            variant="contained"
            color="primary"
            disableRipple
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChatPanel;
